import { Card, CardActions, CardContent, CardMedia, Typography, useTheme } from '@mui/material';
import { Card as TCard } from '@/types/generated';
import { getResourceCardStyles, getResourceCardContentStyles, getResourceCardActionStyles, getResourceCardTitle } from './ResourceCardTile.styles';
import { getCardMediaStyles } from '../CardItem/CardItem.styles';
import AspectRatioPaddingBox from '@shared-components/components/AspectRatioPaddingBox';
import ContentfulButton from '@shared-components/components/ContentfulButton';
import ImageAsset from '@shared-components/components/ImageAsset';
import { IImage } from '@shared-components/components/ImageAsset/ImageAsset';
import RichText, { RichTextContent } from '@shared-components/components/RichText/RichText';
import { HeadingTag } from '@shared-components/types';
import { textToJumpLink } from '@utils';
import getHeadingVariant from '@utils/resolveContentfulHeadingVariant';
interface IResourceCardItem {
  card: TCard;
  isSmallScreen: boolean;
  useMediaRoundedCorners?: boolean;
}

/**
 * Safely render a value as a string ReactNode
 * Filters out objects and other invalid ReactNode types
 */
function safeTextNode(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (value === null || value === undefined) {
    return null;
  }
  // Log invalid values for debugging
  console.warn('ResourceCardTile: Attempted to render invalid text value:', value);
  return null;
}
export default function ResourceCardTitle({
  card,
  isSmallScreen,
  useMediaRoundedCorners = false
}: Readonly<IResourceCardItem>) {
  const theme = useTheme();
  const cardButtons = card?.buttonsCollection?.items;
  return <Card data-testid={`resource_card_tile_${card.sys.id}`} id={textToJumpLink(card.title)} sx={getResourceCardStyles({
    backgroundColor: card.backgroundColor
  })} data-sentry-element="Card" data-sentry-component="ResourceCardTitle" data-sentry-source-file="ResourceCardTile.tsx">
			{card.imageAsset && <AspectRatioPaddingBox aspectRatio="5:4" contentfulAspectRatio={card.imageRatio} paddingBoxSx={{
      ...getCardMediaStyles(useMediaRoundedCorners),
      height: 'auto'
    }}>
					<CardMedia component={card.imageAsset && card.buttonUrl ? 'a' : 'div'} href={card.buttonUrl || undefined} sx={{
        cursor: card.imageAsset && card.buttonUrl ? 'pointer' : 'default',
        overflow: 'hidden',
        ...getCardMediaStyles(useMediaRoundedCorners)
      }} data-testid={`resource_card_tile_image_asset_link_${card.sys.id}`}>
						<ImageAsset desktopImage={card.imageAsset.desktopImage as IImage} mobileImage={card.imageAsset.mobileImage as IImage} isBackgroundImage={false} dataTestId={`resource_card_img_asset_item_background_${card.sys.id}`} />
					</CardMedia>
					<CardContent sx={getResourceCardContentStyles(isSmallScreen, theme, card.cardTextAlignment)}>
						{safeTextNode(card.title) && <Typography variant={getHeadingVariant(card.titleHtag as HeadingTag)} sx={getResourceCardTitle(theme)} data-testid={`resource_card_tile_title_${card.sys.id}`}>
								{safeTextNode(card.title)}
							</Typography>}
						{safeTextNode(card.description) && <Typography color={'text.light'} variant="bodySmallBook" data-testid={`resource_card_tile_description_${card.sys.id}`} textAlign={card.cardTextAlignment === 'center' ? 'center' : 'left'}>
								{safeTextNode(card.description)}
							</Typography>}
						{card.richTextDescription && <RichText content={card.richTextDescription.json as RichTextContent} links={card.richTextDescription.links} docProps={{
          textAlign: card.cardTextAlignment === 'center' ? 'center' : 'left',
          color: 'text.light',
          variant: 'bodySmallBook'
        }} />}

						{cardButtons && cardButtons.length > 0 ? cardButtons.map(button => button ? <CardActions key={button?.sys?.id} sx={getResourceCardActionStyles({
          cardActionAlignment: card.cardTextAlignment,
          isSmallScreen: isSmallScreen
        })}>
											<ContentfulButton {...button} defaultVariant="secondaryDefault" dataTestId={`resource_card_tile_button_${button?.name || ''}`} />
										</CardActions> : null) : null}
					</CardContent>
				</AspectRatioPaddingBox>}
		</Card>;
}